// The library a page draws from: the components built from the source, and
// components made on the page out of them. A made component is a hidden
// root in the page's tree, `definition`, holding the blocks it was made
// from; it exposes some of their text and props as its own props. An
// instance is a node naming it, whose props are only its overrides, so it
// follows every edit to the definition except where it was changed.
// meets: compose/new, compose/instances
import type { Delta } from "loro-crdt";
import { checkProp, components, type Prop } from "./components";
import type { NodeJSON, TreeID } from "./doc";

export type Expose = Record<string, { node: TreeID; field: string }>; // field: "text" or "prop:<key>"

export type Entry = {
  name: string;
  label: string;
  props: Record<string, Prop>;
  text?: boolean;
  children?: boolean;
  made?: NodeJSON; // the definition, for a component made on the page
};

export const NAME = /^[A-Z][A-Za-z0-9]{0,30}$/;

export const plain = (d: Delta<string>[] = []) => d.map((o) => ("insert" in o ? o.insert ?? "" : "")).join("");

function find(nodes: NodeJSON[], id: TreeID): NodeJSON | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    const f = find(n.children, id);
    if (f) return f;
  }
}

// A made component's props: each exposed field, with the definition's
// current value as its default.
export function exposed(def: NodeJSON): Record<string, Prop> {
  const out: Record<string, Prop> = {};
  for (const [name, { node, field }] of Object.entries((def.props.expose ?? {}) as Expose)) {
    const n = find(def.children, node);
    if (!n) continue;
    if (field === "text") out[name] = { kind: "string", default: plain(n.text), max: 2000 };
    else if (field.startsWith("prop:")) {
      const p = components[n.type]?.props[field.slice(5)];
      if (p) out[name] = { ...p, default: (n.props[field.slice(5)] ?? p.default) as never };
    }
  }
  return out;
}

export function library(defs: NodeJSON[]): Record<string, Entry> {
  const out: Record<string, Entry> = {};
  for (const [name, c] of Object.entries(components)) out[name] = { name, label: c.label, props: c.props, text: c.text, children: c.children };
  for (const d of defs) {
    const name = String(d.props.name ?? "");
    if (!NAME.test(name) || out[name]) continue;
    out[name] = { name, label: name, props: exposed(d), made: d };
  }
  return out;
}

// What an instance draws: the definition's blocks, with the instance's
// overrides written over the fields they expose.
export function instance(def: NodeJSON, overrides: Record<string, unknown>): NodeJSON[] {
  const props = exposed(def);
  const expose = (def.props.expose ?? {}) as Expose;
  const set = new Map<TreeID, (n: NodeJSON) => NodeJSON>();
  for (const [name, { node, field }] of Object.entries(expose)) {
    const v = overrides[name];
    const p = props[name];
    if (v === undefined || !p || !checkProp(p, v)) continue;
    if (field === "text" && typeof v === "string") set.set(node, (n) => ({ ...n, text: v ? [{ insert: v }] : [] }));
    else if (field.startsWith("prop:")) set.set(node, (n) => ({ ...n, props: { ...n.props, [field.slice(5)]: v } }));
  }
  const walk = (n: NodeJSON): NodeJSON => {
    const m = set.get(n.id)?.(n) ?? n;
    return { ...m, children: m.children.map(walk) };
  };
  return def.children.map(walk);
}
